import React from 'react'
import styled from '@emotion/styled'
import { COLORS } from 'parameters/index'
import { SearchWithExtras, Trivia } from '../../../../../../generated/api'
import { determineSearchType, sanitizeTrivia } from './helpers'

import { ProductIcon } from 'experiences/search/components/icons/ProductIcon'
import {
	CustomDependentLoadingSkeleton,
	DependentLoadingSkeleton,
	generateComponents,
	sk,
	SkeletonType
} from 'components/patterns/Skeleton'
import { getColorsFromSearchType } from '../../../utils/lib'

interface Props {
	search?: SearchWithExtras
	showDate?: boolean
	children?: React.ReactNode
	constrainTrivia?: boolean
}

export const SearchSummaryContent = ({
	search,
	showDate = true,
	children,
	constrainTrivia = false
}: Props): JSX.Element => {
	const searchType = determineSearchType(search)
	const summary = search?.summary

	const TriviaItem: React.FC<React.PropsWithChildren<{ trivia: Partial<Trivia> }>> = ({ trivia }): JSX.Element => {
		return (
			<Item constrain={constrainTrivia}>
				<TriviaValue>{sk(trivia?.value, SkeletonType.title)}</TriviaValue>
				<TriviaTitle>{sk(trivia?.title)}</TriviaTitle>
			</Item>
		)
	}

	return (
		<CardContent>
			<CardTop>
				<ImageWrapper color={summary ? getColorsFromSearchType(searchType).secondary : 'transparent'}>
					<DependentLoadingSkeleton dependency={summary} type={SkeletonType.square}>
						<>
							{(summary?.offerCount || 0) > 0 && (
								<Circle>
									<span>{summary?.offerCount}</span>
								</Circle>
							)}
							<ProductIcon product={summary?.products?.[0]} state={searchType} />
						</>
					</DependentLoadingSkeleton>
				</ImageWrapper>
				<Text>
					<Title>{sk(summary?.intent)}</Title>
					<SubTitle>{sk(summary?.products?.join(' | '))}</SubTitle>
				</Text>
			</CardTop>
			<Items>
				<CustomDependentLoadingSkeleton
					customSkeleton={generateComponents(
						key => (
							<TriviaItem key={key} trivia={{}} />
						),
						4
					)}
					dependency={search}
				>
					{sanitizeTrivia(search, showDate).map((trivia, index) => (
						<TriviaItem trivia={trivia} key={index} />
					))}
				</CustomDependentLoadingSkeleton>
			</Items>
			{children}
		</CardContent>
	)
}

const CardContent = styled.div`
	display: flex;
	width: 100%;
	flex-direction: column;
	justify-content: space-between;
`

const CardTop = styled.div`
	display: flex;
	margin-bottom: 24px;
`

const Items = styled.div`
	display: flex;
	flex-wrap: wrap;
	width: 100%;
	margin-bottom: auto;
`

const Item = styled.div<{ constrain: boolean }>`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	flex-grow: 1;
	margin-bottom: 7px;
	padding: 5px;
	width: ${(props): string => (props.constrain ? '50%' : 'unset')};

	p {
		white-space: normal;
	}
`

const TriviaValue = styled.p`
	font-size: 16px;
	font-weight: 600;
`

const TriviaTitle = styled.p`
	font-size: 14px;
	font-weight: normal;
	color: ${COLORS.SPANISH_GRAY};
`

const ImageWrapper = styled.div<{ color: string }>`
	width: 48px;
	height: 48px;
	min-width: 48px;
	border-radius: 8px;
	background-color: ${(props): string => props.color};
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 16px;
	position: relative;
`

const Circle = styled.div`
	width: 20px;
	height: 20px;
	border-radius: 50%;
	position: absolute;
	top: -8px;
	right: -8px;
	background-color: ${COLORS.KSU_PURPLE};
	font-size: 12px;
	font-weight: bold;
	color: ${COLORS.WHITE};
	display: flex;
	align-items: center;
	justify-content: center;
`

const Text = styled.div`
	display: flex;
	justify-content: center;
	flex-direction: column;
	width: 80%;
`

const Title = styled.h3`
	display: block;
	font-size: 20px;
	font-weight: 800;
	margin-bottom: 5px;
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
`

const SubTitle = styled.span`
	font-size: 14px;
	font-weight: normal;
	color: ${COLORS.SPANISH_GRAY};
`
